<template>
  <div v-if="isAdmin" :id="textId" class="Editor" @click="focused" v-clickoutside="unfocused">
    <div class="Menu" v-if="showMenu">
      <template v-for="(button, index) in buttons" :key="index">
        <div class="divider" v-if="button.type === 'divider'" />
        <menu-item v-else v-bind="button" />
      </template>
    </div>
    <editor-content class="EditorContent" toggleWrap="true" :spellcheck="showMenu" :editor="editor"></editor-content>
  </div>
  <div v-else>
    <span v-if="content" v-html="content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import TextAlign from '@tiptap/extension-text-align';
import Cookies from "js-cookie";

import { updateText, getText } from '@/firebase';
import MenuItem from '@/Components/EditorMenuItem.vue';

/* eslint-disable vue/no-unused-components */
export default {
  components: {
    MenuItem,
    EditorContent,
  },

  data() {
    return {
      editor: null,
      buttons: null,
      showMenu: false,
      content: null,
      isAdmin: Cookies.get("user") ? true : false,
    };
  },
  props: {
    textId: {
      type: String,
      required: true,
    },
  },
  methods: {
    focused() {
      this.showMenu = true;
    },
    async unfocused() {
      if (this.showMenu) {
        this.showMenu = false;
        await updateText(this.textId, this.editor.getHTML());
        this.$notify({ text: 'Text uppdaterad', type: 'success' });
      }
    },
  },

  async mounted() {
    this.content = await getText(this.textId);
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Underline,
        BulletList,
        ListItem,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
      ],
      content: this.content,
    });
    this.buttons = [
      {
        icon: 'bold',
        title: 'Bold',
        action: () => this.editor.chain().focus().toggleBold().run(),
        isActive: () => this.editor.isActive('bold'),
      },
      {
        icon: 'italic',
        title: 'Italic',
        action: () => this.editor.chain().focus().toggleItalic().run(),
        isActive: () => this.editor.isActive('italic'),
      },
      {
        icon: 'underline',
        title: 'Underline',
        action: () => this.editor.chain().focus().toggleUnderline().run(),
        isActive: () => this.editor.isActive('underline'),
      },
      {
        icon: 'strikethrough',
        title: 'Strike',
        action: () => this.editor.chain().focus().toggleStrike().run(),
        isActive: () => this.editor.isActive('strike'),
      },
      {
        type: 'divider',
      },
      {
        icon: 'h-1',
        title: 'Heading 1',
        action: () => this.editor.chain().focus().toggleHeading({ level: 1 }).run(),
        isActive: () => this.editor.isActive('heading', { level: 1 }),
      },
      {
        icon: 'h-2',
        title: 'Heading 2',
        action: () => this.editor.chain().focus().toggleHeading({ level: 2 }).run(),
        isActive: () => this.editor.isActive('heading', { level: 2 }),
      },
      {
        icon: 'h-3',
        title: 'Heading 3',
        action: () => this.editor.chain().focus().toggleHeading({ level: 3 }).run(),
        isActive: () => this.editor.isActive('heading', { level: 3 }),
      },
      {
        type: 'divider',
      },
      {
        icon: 'align-right',
        title: 'Right align',
        action: () => this.editor.chain().focus().setTextAlign('right').run(),
        isActive: () => this.editor.isActive({ textAlign: 'right' }),
      },
      {
        icon: 'align-center',
        title: 'Center align',
        action: () => this.editor.chain().focus().setTextAlign('center').run(),
        isActive: () => this.editor.isActive({ textAlign: 'center' }),
      },
      {
        icon: 'align-left',
        title: 'Left align',
        action: () => this.editor.chain().focus().setTextAlign('left').run(),
        isActive: () => this.editor.isActive({ textAlign: 'left' }),
      },
      {
        type: 'divider',
      },
      {
        icon: 'list-unordered',
        title: 'Bullet List',
        action: () => this.editor.chain().focus().toggleBulletList().run(),
        isActive: () => this.editor.isActive('bulletList'),
      },
      {
        icon: 'list-ordered',
        title: 'Ordered List',
        action: () => this.editor.chain().focus().toggleOrderedList().run(),
        isActive: () => this.editor.isActive('orderedList'),
      },
      {
        type: 'divider',
      },
      {
        icon: 'code-box-line',
        title: 'Code Block',
        action: () => this.editor.chain().focus().toggleCodeBlock().run(),
        isActive: () => this.editor.isActive('codeBlock'),
      },
      {
        icon: 'double-quotes-l',
        title: 'Blockquote',
        action: () => this.editor.chain().focus().toggleBlockquote().run(),
        isActive: () => this.editor.isActive('blockquote'),
      },
      {
        type: 'divider',
      },
      {
        icon: 'separator',
        title: 'Horizontal Rule',
        action: () => this.editor.chain().focus().setHorizontalRule().run(),
      },
      {
        type: 'divider',
      },
      {
        icon: 'format-clear',
        title: 'Clear Format',
        action: () => this.editor.chain().focus().clearNodes().unsetAllMarks()
          .run(),
      },
      {
        type: 'divider',
      },
      {
        icon: 'arrow-go-back-line',
        title: 'Undo',
        action: () => this.editor.chain().focus().undo().run(),
      },
      {
        icon: 'arrow-go-forward-line',
        title: 'Redo',
        action: () => this.editor.chain().focus().redo().run(),
      },
    ];
  },

  beforeUnmount() {
    this.editor.destroy();
  },
};
</script>

<style>
.EditorContent {
  min-height: 50px;
}
.Editor:focus {
  border-radius: 0.3rem;
  padding: 0.5rem;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}
/* Basic editor styles */
.ProseMirror > * + * {
  margin-top: 0.75em;
}
.ProseMirror:focus {
  outline: none;
}
.ProseMirror ul,
.ProseMirror ol {
  padding: 0 1rem;
}
.ProseMirror h1,
.ProseMirror h2,
.ProseMirror h3,
.ProseMirror h4,
.ProseMirror h5,
.ProseMirror h6 {
  line-height: 1.1;
}
.ProseMirror code {
  background-color: rgba(97, 97, 97, 0.1);
  color: #616161;
}
.ProseMirror pre {
  background: #0d0d0d;
  color: #fff;
  font-family: "JetBrainsMono", monospace;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
.ProseMirror pre code {
  color: inherit;
  padding: 0;
  background: none;
  font-size: 0.8rem;
}
.ProseMirror img {
  max-width: 100%;
  height: auto;
}
.ProseMirror blockquote {
  padding-left: 1rem;
  border-left: 2px solid rgba(13, 13, 13, 0.1);
}
.ProseMirror hr {
  border: none;
  border-top: 2px solid rgba(13, 13, 13, 0.1);
  margin: 2rem 0;
}
.Menu {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 0px 16px 0 rgba(0, 0, 0, 0.2);
}
.divider {
  width: 2px;
  height: 1.25rem;
  background-color: black;
  opacity: 10%;
  margin-left: 0.5rem;
  margin-right: 0.75rem;
}
.menu-item {
  width: 1.75rem;
  height: 1.75rem;
  color: #0d0d0d;
  border: none;
  background-color: transparent;
  border-radius: 0.4rem;
  padding: 0.25rem;
  margin-right: 0.25rem;
}
.menu-item svg {
  width: 100%;
  height: 100%;
  fill: currentColor;
}
.menu-item.is-active,
.menu-item:hover {
  color: #fff;
  background-color: #0d0d0d;
}
</style>
